import { useTranslation } from "react-i18next";
import { Integration } from "/app/src/models";
import Box from "/app/src/components/generic/components/box";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { useCallback, useMemo } from "react";
import { Checkbox, Select, SubmitButton, Form } from "formik-antd";
import useMultipleSettings from "/app/src/hooks/useMultipleSettings";
import {
  convertCSVtoArray,
  convertArrayToCSV,
  convertStringToBoolean,
} from "/app/src/helpers";
import { materialFieldOptions } from "/app/src/constants/types";
import { IconToolTip } from "/app/src/components/icons/IconBuilder";

interface FormValues {
  materialFields: string[] | string | undefined;
  disableRevisions: boolean | undefined;
}

/**
 * GenericSettings component renders a settings form for a given integration.
 *
 * @param {Object} props - Component props.
 * @param {Integration} props.integration - The integration object containing settings.
 * @returns {JSX.Element} The settings form component.
 */
export default function GenericSettings({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();

  const { submitHandler, materialFields, disableRevisions } =
    useMultipleSettings({
      integrationId: integration.id,
      settingTypes: ["materialFields", "disableRevisions"],
    });

  const onSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      values.materialFields = convertArrayToCSV(values.materialFields);
      await submitHandler(values);
      actions.resetForm();
    },
    [submitHandler],
  );

  const materialFieldsLabel = useMemo(() => {
    return (
      <>
        {t("translation:material_fields")}{" "}
        <IconToolTip
          content={t("translation:material_fields_tip")}
          className="ml-1"
        />
      </>
    );
  }, [t]);

  /**
   * Renders the settings form UI.
   *
   * @param {FormikProps<FormValues>} props - Formik form properties.
   * @returns {JSX.Element} The settings form component.
   */
  const genericSettingsForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isSubmitting }) => (
        <Form layout="vertical">
          <div className="flex justify-between items-center">
            <h2 className="text-[18px] font-medium">
              {t("translation:generic_settings")}
            </h2>
            <SubmitButton type="primary" disabled={!dirty || isSubmitting}>
              {t("translation:save")}
            </SubmitButton>
          </div>
          <div className="flex space-x-[16px]">
            <Form.Item
              name="materialFields"
              label={materialFieldsLabel}
              hasFeedback={false}
              className="w-[425px]"
            >
              <Select mode="multiple" name="materialFields" size="large">
                {materialFieldOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="disableRevisions"
              label={t("translation:hide_revisions")}
              hasFeedback={false}
            >
              <Checkbox name="disableRevisions" />
            </Form.Item>
          </div>
        </Form>
      ),
      [materialFieldsLabel, t],
    );

  return (
    <Box>
      <Formik
        initialValues={{
          materialFields: convertCSVtoArray(materialFields),
          disableRevisions: convertStringToBoolean(disableRevisions),
        }}
        onSubmit={onSubmit}
        enableReinitialize
        component={genericSettingsForm}
      />
    </Box>
  );
}
